import React, {PureComponent} from "react"
import {Row, Col} from "react-bootstrap"
import {
  ResponsiveContainer,
  ComposedChart,
  Line,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as ChartTooltip,
  Cell,
  Scatter,
  ZAxis
} from "recharts"
import moment from "moment"

import {hangPromise} from "@irati/utils/promise"
import Loading from "@irati/ui/src/Loading"
import authentication from "@irati/auth"
import {injectIntl, InjectIntl, FormattedMessage} from "@irati/utils/lang"
import {Label, H2, H4, Body} from "@irati/ui/src/Typography"
import {Select} from "@irati/ui/src/FormItem"
import adminReportingService from "@irati/adminReporting/services/adminReporting"
import theme from "@irati/ui/src/global/theme"
import Icon, {IconTypes} from "@irati/ui/src/Icon"
import Flex from "@irati/ui/src/Flex"
import adminTracking, {Filter, ViewType} from "@irati-tracking/admin/admin"
import {BenchmarkReport, UsageReport} from "@irati/types"
import {Tooltip} from "@irati/ui/src/Tooltip"
import Button from "@irati/ui/src/Button"
import GenericModal from "@irati/ui/src/GenericModal"

import ShareLink from "../../../components/ShareLink"
import {ChartWrapper, Wrapper,  YAxisLabel, XAxisLabel} from "./styled"
import BenchmarkText from "./BenchmarkText"
import BenchmarkTooltip from "./BenchmarkTooltip"

const LAST_LOGIN = "LAST_LOGIN"
const ALL_INDUSTRIES = "All Industries"

interface Props extends InjectIntl{
  source: string
}

interface State {
  load: Promise<void> | null,
  submit: Promise<void> | null,
  reports: UsageReport[] | [],
  benchmark: BenchmarkReport[] | [],
  selectedBenchmark: string,
  defaultBenchmark: string | null,
  showModal: boolean,
  focusBarIndex: number | null,
  mouseLeave: boolean,
  isAnimating: boolean
}

class BenchmarkGraph extends PureComponent<Props, State> {
  state: State = {
    load: hangPromise(),
    submit: null,
    reports: [],
    benchmark: [],
    selectedBenchmark: ALL_INDUSTRIES,
    defaultBenchmark: null,
    showModal: false,
    focusBarIndex: null,
    mouseLeave: true,
    isAnimating: true
  }

  componentDidMount(){
    const benchmarkReports = adminReportingService.getBenchmarkReports().then(({reports}) => {
      this.setState({benchmark: reports})
    })
    const topics = [LAST_LOGIN]
    const uuid = authentication.business().uuid
    const usageReports = adminReportingService.getUsageReports(uuid, topics).then(({reports}) => {
      this.setState({reports: reports[0].points})
    })
    const defaultBenchmarkLoad = adminReportingService.getBusinessSettings(uuid).then(({industry}) => {
      this.setState({defaultBenchmark: industry, showModal: !industry})
      if (industry) {
        this.setState({selectedBenchmark: industry})
      }
    })

    const load = Promise.all([benchmarkReports, usageReports, defaultBenchmarkLoad])
    this.setState({load})
  }

  handleSetDefaultClick = () => {
    adminTracking.analyticsSetDefaultBenchmark()
    this.setState({showModal: true})
  }

  handleChangeDefaultClick = () => {
    adminTracking.reporting({
      source:this.props.source,
      type: "overall_benchmark",
      filter: Filter.industry,
      action: "edit_defaultIndustry"})
    this.setState({showModal: true})
  }

  handleUpdate = (selectedBenchmark: string) => {
    adminTracking.reporting({
      source: this.props.source,
      type: "overall_benchmark",
      viewType: selectedBenchmark === this.state.defaultBenchmark ? ViewType.default : ViewType.filtered,
      filter: Filter.industry})
    this.setState({selectedBenchmark, isAnimating: true})
  }

  handleDefaultSelect = (defaultBenchmark: string) => {
    adminTracking.reporting({
      source: this.props.source,
      type: "overall_benchmark",
      viewType: ViewType.filtered,
      filter: Filter.industry})
    this.setState({defaultBenchmark})
  }

  handleDefaultUpdate = () => {
    const {defaultBenchmark} = this.state
    const submit = adminReportingService.updateBusinessSettings(authentication.business().uuid, defaultBenchmark).then(() => {
      this.setState({showModal: false})
    })
    this.setState({submit, selectedBenchmark: defaultBenchmark})
  }

  buildData = () => {
    const {reports, benchmark, selectedBenchmark} = this.state
    const currentBenchmark = benchmark.find(({name}) => name === selectedBenchmark)

    return reports.map((report) => {
      return {
        date:  moment(report.date).subtract(1, "months").format("MMM"),
        value: Math.round(report.value),
        benchmark: currentBenchmark?.points.find(({date}) => date === report.date)?.value
      }
    })
  }

  buildBenchmarkOptions = () => {
    const {benchmark} = this.state
    return benchmark.map(({name}) => ({
      label: name,
      value: name
    }))
  }

  onHoverState = (state: any) => {
    if (!this.state.isAnimating) {
      if (state.isTooltipActive) {
        this.setState({focusBarIndex: state.activeTooltipIndex, mouseLeave: false})
      } else {
        this.setState({focusBarIndex: null, mouseLeave: true})
      }
    }
  }

  render() {
    const {load, selectedBenchmark, defaultBenchmark, showModal, submit, focusBarIndex, mouseLeave} = this.state
    const {formatMessage} = this.props

    const data = this.buildData()
    const lastMonth = data[data.length - 2]
    const companyLastMonth = Math.round(lastMonth?.value || 0)
    const benchmarkLastMonth = Math.round(lastMonth?.benchmark || 0)
    const twoMonthsPrior = Math.round(data[data.length - 3]?.value)
    const companyLastMonthDifferenceFromPrevious = companyLastMonth - twoMonthsPrior
    const isCompanyDifferenceNegative = Math.sign(companyLastMonthDifferenceFromPrevious) === -1
    const iconColor = isCompanyDifferenceNegative ? theme.colors.tertiary.r_400 : theme.colors.success

    return (
      <>
        <Flex justifyContent="flex-end">
          {defaultBenchmark ?
            <>
              <ShareLink />
              <Button
                id="adminSection_reportingPage_benchmark_changeDefault_button"
                marginLeft
                tertiary
                cta
                onClick={this.handleChangeDefaultClick}>
                {formatMessage({id: "admin.components.benchmark.changeDefault.button"})}
              </Button>
            </> :
            <ShareLink />
          }
        </Flex>

        <H2 marginBottom={1}>
          <FormattedMessage id="admin.components.benchmark.title" />
        </H2>
        <Body marginBottom={1}>
          <FormattedMessage id="admin.components.benchmark.text" />
        </Body>
        {!defaultBenchmark &&
          <Body marginBottom={4}>
            <FormattedMessage
              id="admin.components.benchmark.setDefault.text"
              values={{buttonText: <Button
                tertiary
                cta
                onClick={this.handleSetDefaultClick}>
                {formatMessage({id: "admin.components.benchmark.setDefault.button"})}
              </Button>}} />
          </Body>
        }
        <Loading load={load}>
          <Wrapper marginBottom={5} id="adminSection_industryBenchmarkGraph">
            <Row>
              <Col xs={4}>
                <Row>
                  <Select
                    id="adminSection__reporting_industryBenchmarkFilterId"
                    label={formatMessage({id: "admin.components.benchmark.select.label"})}
                    isClearable={false}
                    value={selectedBenchmark}
                    options={this.buildBenchmarkOptions()}
                    onChange={this.handleUpdate} />
                </Row>
              </Col>
            </Row>
            <Flex margin={[4, 0]}>
              <Flex direction="column" marginRight={3}>
                <Label marginBottom={2}>
                  <FormattedMessage id="admin.components.benchmark.companyLastMonth" />
                </Label>
                <Flex alignItems="flex-end">
                  <H4 marginRight={1}>{companyLastMonth}%</H4>
                  <Icon
                    margin={[0, 0.5, 0.5, 0]}
                    color={iconColor}
                    icon={isCompanyDifferenceNegative ? IconTypes.chevronDown : IconTypes.chevronUp} />
                  <Label marginBottom={0.5} small color={iconColor}>{Math.abs(companyLastMonthDifferenceFromPrevious)}%</Label>
                </Flex>
              </Flex>
              <Flex direction="column">
                <Label marginBottom={2}>
                  <FormattedMessage id="admin.components.benchmark.benchmarkLastMonth" />
                </Label>
                <H4>{benchmarkLastMonth}%</H4>
              </Flex>
            </Flex>
            <ChartWrapper index={focusBarIndex + 1} onMouseLeave={() => this.setState({focusBarIndex: null, mouseLeave: true})}>
              <ResponsiveContainer width="100%" height={500}>
                <ComposedChart
                  width={500}
                  height={400}
                  data={data}
                  margin={{
                    top: 0,
                    right: 0,
                    bottom: 0,
                    left: 0
                  }}
                  onMouseMove={(state: any) => this.onHoverState(state)}>
                  <CartesianGrid vertical={false} />
                  <XAxis
                    dataKey="date"
                    padding={{left: -30, right: 50}}
                    axisLine={false}
                    tickLine={false} />
                  <YAxis
                    tickCount={6}
                    tickFormatter={(item) => `${item}%`}
                    padding={{bottom: 10}}
                    axisLine={false}
                    tickLine={false} />
                  <ZAxis dataKey="value" range={[30, 30]} />
                  <ChartTooltip content={<BenchmarkTooltip  />} />
                  <Bar
                    id="adminSection_companyBenchmarkGraphBar"
                    dataKey="value"
                    barSize={40}>
                    {data.map((entry, index) => {
                      return (
                        <Cell
                          key={index}
                          fill={focusBarIndex === index  || mouseLeave ? theme.colors.primary2 : theme.colors.primary2_100} />
                      )
                    })}
                  </Bar>
                  <Line
                    onAnimationEnd={() => this.setState({isAnimating: false})}
                    type="monotone"
                    dataKey="benchmark"
                    stroke={theme.colors.primary1}
                    strokeDasharray="4 4"
                    dot={<BenchmarkText text={selectedBenchmark} />} />
                  {!mouseLeave && <Scatter dataKey="value" shape="circle" stroke={theme.colors.primary2} fill={theme.colors.greys.white} />}
                </ComposedChart>
              </ResponsiveContainer>
            </ChartWrapper>
            <Tooltip tooltipChildren={formatMessage({id: "admin.components.benchmark.yaxisTooltip"})}>
              <YAxisLabel small>
                <FormattedMessage id="admin.components.benchmark.yaxis" />
              </YAxisLabel>
            </Tooltip>
            <XAxisLabel small>
              <FormattedMessage id="admin.components.benchmark.xaxis" />
            </XAxisLabel>
          </Wrapper>
        </Loading>
        <GenericModal
          show={showModal}
          title={formatMessage({id: "admin.components.benchmark.setDefaultModal.title"})}
          load={submit}
          proceedButtonText={formatMessage({id: "admin.components.benchmark.setDefault.button"})}
          dismissButtonText={formatMessage({id: "admin.components.benchmark.setDefaultModal.back"})}
          onConfirm={this.handleDefaultUpdate}
          onHide={() => this.setState({showModal: false})}>
          <Body marginBottom={6}>
            <FormattedMessage id="admin.components.benchmark.setDefaultModal.text" />
          </Body>
          <Row>
            <Select
              label={formatMessage({id: "admin.components.benchmark.setDefaultModal.select.label"})}
              isClearable={false}
              id="adminSection_setDefaultIndustryField"
              value={defaultBenchmark}
              options={this.buildBenchmarkOptions()}
              onChange={this.handleDefaultSelect} />
          </Row>
        </GenericModal>
      </>
    )
  }
}

export default injectIntl(BenchmarkGraph)
